import { useEffect, useMemo, useRef, useState } from "react";
import { ChatProvider, useChat } from "@/chat/ChatContext";
import { MessageList } from "@/chat/MessageList";
import { ChatInput } from "@/chat/ChatInput";
import { qrApi, type QrSessione } from "@/api/qr";
import { PassoBar, QrTesta } from "./QrTesta";
import { useCaricamento, type CaricatoreQr } from "./caricatore";
import { useAnalisiChat, type FonteAnalisi } from "@/components/analisi/useAnalisiChat";
import { StrisciaSpesaVendita } from "@/components/analisi/RiquadroSpesaVendita";
import { VistaCliente } from "@/components/analisi/vistaCliente";

interface Props {
  token: string;
  agenzia: string;
  sessione: QrSessione;
  /** Le foto dei passi Bolletta e Documento, già in salita da quando il cliente le ha scelte
   *  (vive in QrPercorso: un rimontaggio della chat non rimanda nulla). */
  caricatore: CaricatoreQr;
  /** true se `[batch_inizio n=K]` è già stato mandato in questa visita
   *  (persistito da QrPercorso): va mandato una volta sola, mai a ogni tentativo. */
  batchInviato: boolean;
  onBatchInviato: () => void;
}

/** Ultima schermata (spec §8 "Chat"): la chat esistente, in modalità sessione QR.
 *  L'ultima schermata (Richiesta inviata/passata all'agenzia/Manca una firma) la
 *  scrive il Cat come messaggio, non è una schermata dedicata (spec §3.1 punto 6). */
export function QrChat(props: Props) {
  return (
    <ChatProvider sessioneQr={{ ...props.sessione, token: props.token }}>
      <QrChatCorpo {...props} />
    </ChatProvider>
  );
}

function QrChatCorpo({
  token, agenzia, sessione, caricatore, batchInviato, onBatchInviato,
}: Props) {
  const {
    messages, sendMessage, sendBatch, isConnected, isLoading, ensureConnected, sendRaw,
    qrAllegatoFallito, retryAllegatoQr,
  } = useChat();
  const avviatoRef = useRef(false);
  const inCorsoRef = useRef(false);
  const [erroreCaricamento, setErroreCaricamento] = useState(false);
  // Attesa visibile (Alfonso 2026-09-29): finché non arriva il primo messaggio la chat
  // diceva niente per quasi un minuto. `caricate` conta le foto già salite (anche ai passi prima).
  const [avviato, setAvviato] = useState(false);
  const { totale, caricate, analisiIds } = useCaricamento(caricatore);
  // Analisi della bolletta (demo 19/10): parte dal caricamento se è accesa per l'agenzia; si legge con la
  // sessione firmata del cliente. Senza id (pagina ricaricata) si ricostruisce dalla sessione.
  const fonte = useMemo<FonteAnalisi>(() => ({
    leggi: (id) => qrApi.analisi(token, sessione.session_id, id),
    sessione: () => qrApi.analisiSessione(token, sessione.session_id),
  }), [token, sessione.session_id]);
  const analisi = useAnalisiChat(analisiIds, { fonte });

  async function consegna() {
    if (inCorsoRef.current) return;
    inCorsoRef.current = true;
    setErroreCaricamento(false);
    setAvviato(true);
    try {
      if (!batchInviato) {
        // [batch_inizio n=K] come startFromWizard: dichiara quante pagine di
        // bolletta aspettare prima che il Cat risponda con le offerte (K conta
        // solo la bolletta). Una volta sola per visita: un tentativo dopo un
        // caricamento fallito non lo rimanda (altrimenti il Cat conterebbe due
        // batch per la stessa pratica).
        const k = caricatore.numeroBolletta();
        if (k) sendRaw(`[batch_inizio n=${k}]`);
        onBatchInviato();
      }
      // Solo DOPO il batch: il portale passa al Cat le foto già salite, nell'ordine (bolletta, poi documento).
      // Mai prima: il Cat leggerebbe la pagina 1 da sola e presenterebbe le offerte su una bolletta parziale.
      await caricatore.consegna();
    } catch {
      setErroreCaricamento(true);
    } finally {
      inCorsoRef.current = false;
    }
  }

  useEffect(() => { ensureConnected(); }, [ensureConnected]);

  useEffect(() => {
    if (!isConnected || avviatoRef.current) return;
    if (!caricatore.daConsegnare()) return; // tutto già passato al Cat (chat riaperta)
    avviatoRef.current = true;
    consegna();
    // Solo `isConnected` in dep (regola CLAUDE.md sul deps-churn): il caricatore è
    // stabile per la sessione, avviatoRef blocca i rilanci; il retry manuale
    // richiama consegna() direttamente, non passa da questo effetto.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected]);

  return (
    <div className="min-h-dvh flex flex-col bg-surface">
      <QrTesta agenzia={agenzia} />
      <div className="w-full max-w-md mx-auto flex-1 flex flex-col">
        <PassoBar passo={3} testo="3 di 3 · Offerta e dati" />
        {(erroreCaricamento || qrAllegatoFallito) && (
          <div className="mx-3 mt-3 space-y-2" role="alert">
            <p className="text-lg text-red-700">
              Il caricamento non è riuscito. Riprovi tra poco o chieda al banco.
            </p>
            <button
              type="button"
              onClick={() => {
                if (!erroreCaricamento) return retryAllegatoQr();
                caricatore.riprova();
                consegna();
              }}
              className="text-lg font-semibold text-brand-dark underline"
            >
              Nuovo tentativo
            </button>
          </div>
        )}
        {/* overflow-clip, non hidden: la striscia dell'analisi resta agganciata in alto mentre scorre la pagina
            (sul QR scorre la pagina intera, non la sola chat: prova dal vivo del 18/10) */}
        <div className="flex-1 bg-card flex flex-col overflow-clip mt-3 mx-3 mb-3 rounded-xl border border-border">
          <VistaCliente.Provider value={true}><StrisciaSpesaVendita voci={analisi} sempre agganciata /></VistaCliente.Provider>
          {avviato && !erroreCaricamento && messages.length === 0 && (
            <p role="status" className="flex items-center gap-3 px-4 pt-4 text-lg text-muted-foreground">
              <span className="flex gap-1" aria-hidden="true">
                <span className="w-2 h-2 rounded-full bg-brand motion-safe:animate-bounce" />
                <span className="w-2 h-2 rounded-full bg-brand motion-safe:animate-bounce [animation-delay:150ms]" />
                <span className="w-2 h-2 rounded-full bg-brand motion-safe:animate-bounce [animation-delay:300ms]" />
              </span>
              {caricate < totale
                ? `Carico le foto: ${caricate + 1} di ${totale}`
                : "Leggo la bolletta e il documento…"}
            </p>
          )}
          <MessageList messages={messages} isLoading={isLoading} />
          <ChatInput onSend={sendMessage} onSendBatch={sendBatch} />
        </div>
      </div>
    </div>
  );
}
